import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, User, Briefcase, Mail, GraduationCap } from 'lucide-react';

function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { to: '/', label: 'HOME', icon: Home },
    { to: '/about', label: 'ABOUT', icon: User },
    { to: '/projects', label: 'PROJECTS', icon: Briefcase },
    { to: '/stage', label: 'INTERNSHIP', icon: GraduationCap },
    { to: '/contact', label: 'CONTACT', icon: Mail },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.includes(path);
  };


  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  return (
    <nav
      className={`fixed bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 backdrop-blur-2xl rounded-2xl sm:rounded-3xl px-2 sm:px-4 py-2 sm:py-3 border transition-all duration-300 max-w-[95vw] ${
        scrolled
          ? 'bg-black/60 border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
          : 'bg-black/70 border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.2)]'
      }`}
    >
      <div className="flex items-center gap-1 sm:gap-2">
        {/* Logo */}
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg 
        sm:rounded-xl flex items-center justify-center text-black text-lg sm:text-xl 
        font-permanent-marker transition-transform cursor-pointer shadow-lg">
          BS
        </div>
        <div className="w-px h-6 sm:h-8 bg-white/10 mx-1 sm:mx-2"></div>

        {/* Navigation Items */}
        <div
          ref={navRef}
          className="flex items-end gap-0.5 sm:gap-1 relative"
          onMouseLeave={handleMouseLeave}
        >
          {navItems.map((item, index) => {
            const active = isActive(item.to);
            const Icon = item.icon;

            return (
              <Link
                key={item.to}
                to={item.to}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
                className="relative group"
              >
                <div
                  className={`
                    relative flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl
                    transition-all duration-700 ease-in-out
                    ${hoveredItem === index ? 'scale-110 -translate-y-1' : 'scale-100'}
                    ${active ? '' : 'hover:bg-white/5'}
                  `}
                  style={{ transformOrigin: 'bottom center' }}
                >
                  {/* Triangle Light Bar for Active Item */}
                  {active && (
                    <div className="absolute -top-1 sm:-top-2 left-1/2 -translate-x-1/2 w-8 sm:w-11 h-[3px] sm:h-[5px] pointer-events-none transition-all duration-700 ease-in-out">
                      <div className="w-full h-full bg-white rounded-full shadow-[0_50px_15px_rgba(255,255,255,0.5)] transition-all duration-700 ease-in-out" />
                      <div
                        className="absolute left-[-30%] top-[3px] sm:top-[5px] w-[160%] h-10 sm:h-14 transition-all duration-700 ease-in-out"
                        style={{
                          clipPath: 'polygon(5% 100%, 25% 0, 75% 0, 95% 100%)',
                          background: 'linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, transparent 100%)',
                        }}
                      />
                    </div>
                  )}

                  {/* Icon */}
                  <Icon
                    className={`
                      w-5 h-5 sm:w-6 sm:h-6 transition-all duration-700 ease-in-out
                      ${active ? 'text-white' : 'text-white/50 group-hover:text-white/80'}
                    `}
                  />

                  {/* Label on hover */}
                  {hoveredItem === index && (
                    <span className="hidden sm:block text-[9px] font-silkscreen font-bold tracking-wider whitespace-nowrap text-white/90 mt-1 transition-all duration-300 ease-in-out animate-in fade-in slide-in-from-bottom-2">
                      {item.label}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export default Navigation;